/**
 * Inteligência's typed edge: `/tenants/{tenantId}/ontology` (api's own dimension/value/proposal
 * routers, tenant-admin only). One scope per screen -- `post` for Posts and Comentários,
 * `profile` for Perfis -- never mixed in one request, so `useOntology` takes it as a parameter
 * rather than the route implying it.
 */

import { z } from 'zod';

export const ontologyScopeSchema = z.enum(['post', 'comment', 'profile']);
export type OntologyScope = z.infer<typeof ontologyScopeSchema>;

/** The three screens `routes.ts` mounts. Posts and Comentários both read the `post` scope;
 *  Perfis reads `profile` -- see `OntologyPage.tsx`'s own doc comment for why the scope is a
 *  parameter rather than implied by the route. */
export type OntologyPageKind = 'posts' | 'comments' | 'profiles';

export const extensionModeSchema = z.enum(['off', 'suggest', 'auto']);
export type ExtensionMode = z.infer<typeof extensionModeSchema>;

export const ontologyValueSchema = z.object({
  id: z.uuid(),
  parent_id: z.uuid().nullable(),
  name: z.string(),
  aliases: z.array(z.string()),
  locked: z.boolean(),
  mentions: z.number().int(),
});
export type OntologyValue = z.infer<typeof ontologyValueSchema>;

export const ontologyDimensionSchema = z.object({
  id: z.uuid(),
  scope: ontologyScopeSchema,
  name: z.string(),
  position: z.number().int(),
  extract: z.boolean(),
  extension: extensionModeSchema,
  sensitive: z.boolean(),
  values: z.array(ontologyValueSchema),
});
export type OntologyDimension = z.infer<typeof ontologyDimensionSchema>;

export const ontologyProposalSchema = z.object({
  id: z.uuid(),
  dimension_id: z.uuid(),
  name: z.string(),
  aliases: z.array(z.string()),
  occurrences: z.number().int(),
  confidence: z.number(),
});
export type OntologyProposal = z.infer<typeof ontologyProposalSchema>;

export const ontologySettingsSchema = z.object({
  ner_posts: z.boolean(),
  ner_comments: z.boolean(),
  profile_recent_posts: z.boolean(),
  profile_in_comments: z.boolean(),
  auto_accept_confidence: z.number().nullable(),
  auto_accept_occurrences: z.number().int().nullable(),
});
export type OntologySettings = z.infer<typeof ontologySettingsSchema>;

export const autoAcceptDefaultsSchema = z.object({
  confidence: z.number(),
  occurrences: z.number().int(),
});
export type AutoAcceptDefaults = z.infer<typeof autoAcceptDefaultsSchema>;

export const ontologyResponseSchema = z.object({
  scope: ontologyScopeSchema,
  dimensions: z.array(ontologyDimensionSchema),
  proposals: z.array(ontologyProposalSchema),
  settings: ontologySettingsSchema,
  auto_accept_defaults: autoAcceptDefaultsSchema,
});
export type OntologyResponse = z.infer<typeof ontologyResponseSchema>;

export const seedResultSchema = z.object({
  added: z.number().int(),
  baseline_approved: z.boolean(),
});
export type SeedResult = z.infer<typeof seedResultSchema>;

/** `DimensionIn`. No zod schema for the outgoing body, matching every other mutation in this
 *  codebase (`useRenameTag`, `useTeamUpdate`) -- `ApiClient`'s parsed methods only parse the
 *  response. */
export interface DimensionInput {
  name?: string;
  extension?: ExtensionMode;
  extract?: boolean;
  position?: number;
}

export interface ValueInput {
  name?: string;
  aliases?: string[];
  /** `null` moves the value to the top level; omitted leaves the parent unchanged. */
  parent_id?: string | null;
}

export type SettingsInput = OntologySettings;
